import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  AutocompleteInput,
  BooleanInput,
  CreateProps,
  FormDataConsumer,
  RadioButtonGroupInput,
  ReferenceInput,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  required,
  useDataProvider,
  useRedirect,
  useRefresh,
} from "react-admin";
import {
  CcaAcademicYearResources,
  GetParam,
  StudentResources,
  sumCentsAmountToDolar,
} from "utils/tools";
import SanitizedGrid from "components/SanitizedGrid";
import CcaAcademicYearInput from "components/CcaAcademicYearInput";
import StudentScheduleStartDateInput from "components/StudentScheduleStartDateInput";
import moment from "moment";
import CustomEndDateInput from "components/CustomEndDateInput";
import { find } from "lodash";
import DaysOfWeekInput from "components/DaysOfWeekInput";
import AddressAutoCompleteInput from "components/AddressAutoCompleteInput";
import { useField } from "react-final-form";
import CommercialDocumentNoteInput from "./components/CommercialDocumentNoteInput";
import InvoiceInput from "./components/InvoiceInput";
import CalculationButton from "./components/CalculationButton";
import { calculationCostUpdateCcaScheduleValidation } from "utils/validation";
import FixedLocationDaysOfWeekInput from "../../components/FixedLocationDaysOfWeekInput";
import { ccaDropOffLocationType } from "./const";
import CcaScheduleDetail from "./components/CcaScheduleDetailColumn";
import CreateWrapper from "../../components/CreateWrapper";

const useStyles = makeStyles({
  table: {
    marginTop: "10px",
    minWidth: 650,
    padding: "1rem",
    marginBottom: "15px",
  },
});

interface InitializeCommercialDocumentsProps {
  result: IResultCcaCalculation;
  index: number;
}

const CcaScheduleCreate = (props: CreateProps) => {
  const [resultCalculations, setResultCalculations] = useState<
    IResultCcaCalculation[]
  >([]);
  const [idCcaAcademicYear, setIdCcaAcademicYear] = useState<number>();
  const [isNotTaking, setIsNotTaking] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [ccaAcademicTerms, setCcaAcademicTerms] = useState<ICcaAcademicTerm[]>(
    [],
  );
  const [ccaStudentSchedules, setCcaStudentSchedules] = useState<
    ICcaStudentSchedule[]
  >([]);
  const [ccaAreas, setCcaAreas] = useState<IArea[]>([]);
  const [fixedLocations, setFixedLocations] = useState<
    ICcaFixedLocationDropOff[]
  >([]);
  const [ccaWaves, setCcaWaves] = useState<IBusSchedule[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isCalculationButtonClicked, setIsCalculationButtonClicked] =
    useState(false);

  const [paymentPercentages, setPaymentPercentages] = useState<
    IPaymentPercentage[]
  >([]);
  const [school, setSchool] = useState<ISchool>();
  const [ccaDropOffType, setCcaDropOffType] = useState<string>();
  const [allowSelectWave, setAllowSelectWave] = useState<boolean>();
  const [isUseOneFixedLocation, setIsUseOneFixedLocation] =
    useState<boolean>(true);

  const param = GetParam();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const classes = useStyles();
  const provider = useDataProvider();

  const format = "DD/MM/YY";

  const id = param.get("studentId");
  const studentResources = StudentResources(Number(id));

  useEffect(() => {
    provider
      .customGetListWithStaticParams(
        `students/${id}/paymentPercentages?Query.Filter=${JSON.stringify({})}`,
      )
      .then(({ data }: { data: IPaymentPercentage[] }) => {
        setPaymentPercentages(data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (idCcaAcademicYear) {
      provider
        .getList("ccaAcademicTerms", {
          filter: { CcaAcademicYearId: Number(idCcaAcademicYear) },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setCcaAcademicTerms(data as ICcaAcademicTerm[]);
        });

      provider
        .getList("ccaAreas", {
          filter: { SchoolId: Number(studentResources.schoolId) },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setCcaAreas(data as IArea[]);
        });

      setLoading(true);
      provider
        .getList("ccaStudentSchedules", {
          filter: {
            StudentId: Number(id),
            CcaAcademicYearId: Number(idCcaAcademicYear),
          },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "StartedAt",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setCcaStudentSchedules(data as ICcaStudentSchedule[]);
          setLoading(false);
        });

      provider
        .getOne("schools", { id: Number(studentResources.schoolId) })
        .then(({ data }) => {
          setSchool(data as ISchool);

          setAllowSelectWave(data.isAllowParentSelectWave);
          if (data.ccaDropOffType !== "both") {
            setCcaDropOffType(data.ccaDropOffType);
          }
        });

      provider
        .getList("ccaFixedLocationDropOffs", {
          filter: { SchoolId: Number(studentResources.schoolId) },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setFixedLocations(data as ICcaFixedLocationDropOff[]);
        });

      provider
        .getList("busSchedules", {
          filter: { SchoolId: Number(studentResources.schoolId), Type: "cca" },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setCcaWaves(data as IBusSchedule[]);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCcaAcademicYear]);

  let breadcrumbValue = [{ link: "", title: "", params: "" }];

  if (param.get("parentId")) {
    breadcrumbValue = [
      {
        title: "Parents",
        link: `/parents/${param.get("parentId")}/show`,
        params: "",
      },
      {
        title: "Students",
        link: `/students/${param.get("studentId")}/show`,
        params: `?parentId=${param.get("parentId")}`,
      },
      {
        title: "Schedules",
        link: `/students/${param.get("studentId")}/show/1`,
        params: `?parentId=${param.get("parentId")}`,
      },
      {
        title: "Update",
        link: "",
        params: "",
      },
    ];
  } else {
    breadcrumbValue = [
      {
        title: "Students",
        link: `/students/${param.get("studentId")}/show`,
        params: "",
      },
      {
        title: "Schedules",
        link: `/students/${param.get("studentId")}/show/1`,
        params: "",
      },
      {
        title: "Update",
        link: "",
        params: "",
      },
    ];
  }

  const breadcrumb = breadcrumbValue;

  const CustomRedirect = ({ data }: any) => {
    redirect(`/students/${data.studentId}/show/1`);
    refresh();
  };

  const CustomToolbar = (props: any) => (
    <Toolbar {...props}>
      <SaveButton
        disabled={
          props.invalid
            ? props.invalid
            : !isLoaded && !isCalculationButtonClicked
        }
        resource="ccaStudentSchedules"
      />
    </Toolbar>
  );

  const transform = async (data: any) => {
    const isFixedLocationMonday =
      data.isMonday && data.dropOffType === "fixed_location";
    const isFixedLocationTuesday =
      data.isTuesday && data.dropOffType === "fixed_location";
    const isFixedLocationWednesday =
      data.isWednesday && data.dropOffType === "fixed_location";
    const isFixedLocationThursday =
      data.isThursday && data.dropOffType === "fixed_location";
    const isFixedLocationFriday =
      data.isFriday && data.dropOffType === "fixed_location";

    data.isTaking = data.isNotTaking === true ? false : true;

    data.startedAt = data.ccaStartedAt;
    data.endedAt = data.ccaEndedAt;

    let fixedLocationMondayId = data.fixedLocationMondayId;
    let fixedLocationTuesdayId = data.fixedLocationTuesdayId;
    let fixedLocationWednesdayId = data.fixedLocationWednesdayId;
    let fixedLocationThursdayId = data.fixedLocationThursdayId;
    let fixedLocationFridayId = data.fixedLocationFridayId;
    if (data.isUseOneFixedLocation === true) {
      fixedLocationMondayId = data.fixedLocationId;
      fixedLocationTuesdayId = data.fixedLocationId;
      fixedLocationWednesdayId = data.fixedLocationId;
      fixedLocationThursdayId = data.fixedLocationId;
      fixedLocationFridayId = data.fixedLocationId;
    }

    if (data.isNotTaking === false) {
      data.detail = {
        doorToDoorAddress:
          data.dropOffType === "door_to_door"
            ? {
                lat: data.ccaLocationData.LATITUDE,
                lng: data.ccaLocationData.LONGITUDE,
                street: data.street,
                unitNumber: data.unitNumber,
                blockNumber: data.blockNumber,
                postalCode: data.postalCode,
                building: data.building,
              }
            : null,
        isMonday: data.isMonday ? true : false,
        fixedLocationMondayId: isFixedLocationMonday
          ? fixedLocationMondayId
          : null,
        waveMondayId: isFixedLocationMonday ? data.waveMondayId : null,
        isTuesday: data.isTuesday ? true : false,
        fixedLocationTuesdayId: isFixedLocationTuesday
          ? fixedLocationTuesdayId
          : null,
        waveTuesdayId: isFixedLocationTuesday ? data.waveTuesdayId : null,
        isWednesday: data.isWednesday ? true : false,
        fixedLocationWednesdayId: isFixedLocationWednesday
          ? fixedLocationWednesdayId
          : null,
        waveWednesdayId: isFixedLocationWednesday ? data.waveWednesdayId : null,
        isThursday: data.isThursday ? true : false,
        fixedLocationThursdayId: isFixedLocationThursday
          ? fixedLocationThursdayId
          : null,
        waveThursdayId: isFixedLocationThursday ? data.waveThursdayId : null,
        isFriday: data.isFriday ? true : false,
        fixedLocationFridayId: isFixedLocationFriday
          ? fixedLocationFridayId
          : null,
        waveFridayId: isFixedLocationFriday ? data.waveFridayId : null,
        isMain:
          data.isMain && data.dropOffType === "door_to_door" ? true : false,
      };
    } else {
      data.detail = null;
    }

    delete data.isNotTaking;
    delete data.dropOffTypeOption;
    delete data.locationData;
    delete data.street;
    delete data.blockNumber;
    delete data.postalCode;
    delete data.building;
    delete data.address;
    delete data.fixedLocationId;
    delete data.isMonday;
    delete data.fixedLocationMondayId;
    delete data.waveMondayId;
    delete data.isTuesday;
    delete data.fixedLocationTuesdayId;
    delete data.waveTuesdayId;
    delete data.isWednesday;
    delete data.fixedLocationWednesdayId;
    delete data.waveWednesdayId;
    delete data.isThursday;
    delete data.fixedLocationThursdayId;
    delete data.waveThursdayId;
    delete data.isFriday;
    delete data.fixedLocationFridayId;
    delete data.waveFridayId;
    delete data.date;
    delete data.isMainAddress;
    delete data.parentId;
    delete data.allowSelectWave;
    delete data.isMain;
    delete data.ccaStartedAt;
    delete data.ccaEndedAt;

    return data;
  };

  const CcaAcademicYearCallback = (value: any) => {
    setIdCcaAcademicYear(value);
  };

  const ccaAcademicYearResources = CcaAcademicYearResources(idCcaAcademicYear);

  const InitializeCommercialDocuments = ({
    result,
    index,
  }: InitializeCommercialDocumentsProps) => {
    const commercialDocumentTagInvoiceMbcRef = useField(
      `commercialDocumentTags[${index}].invoiceMbcRef`,
    );
    const commercialDocumentTagRateChartId = useField(
      `commercialDocumentTags[${index}].ccaRateChartId`,
    );

    const commercialDocumentNoteField = useField(
      `commercialDocumentNotes[${index}].note`,
    );
    const commercialDocumentNoteRateChartField = useField(
      `commercialDocumentNotes[${index}].ccaRateChartId`,
    );

    useEffect(() => {
      if (isCalculationButtonClicked) {
        commercialDocumentTagInvoiceMbcRef.input.onChange(null);
        commercialDocumentTagRateChartId.input.onChange(result.ccaRateChart.id);

        commercialDocumentNoteField.input.onChange(null);
        commercialDocumentNoteRateChartField.input.onChange(
          result.ccaRateChart.id,
        );
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCalculationButtonClicked]);

    return <></>;
  };

  const ccaScheduleDropOffTypeFormatter = (id: string, source: any): string => {
    const item = source.find((type: any) => type.id === id);
    return item ? item.name : "-";
  };

  return (
    <>
      <CreateWrapper
        breadcrumb={breadcrumb}
        headerTitle="Update CCA Schedule"
        transform={transform}
        onSuccess={CustomRedirect}
        {...props}
      >
        <SimpleForm toolbar={<CustomToolbar />}>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Student</Typography>
            </Grid>
            <Grid item xs={3}>
              <ReferenceInput
                label="Select Student"
                source="studentId"
                reference="students"
                disabled
              >
                <SelectInput
                  optionText={(choice: any) =>
                    `${choice.firstName} ${choice.lastName}`
                  }
                />
              </ReferenceInput>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">School Details</Typography>
            </Grid>

            <Grid item xs={4}>
              <ReferenceInput
                label="School"
                source="schoolId"
                reference="schools"
                validate={required()}
                filterToQuery={(searchText) => {
                  if (searchText !== "") return { search: searchText };
                }}
                disabled
              >
                <AutocompleteInput optionText="name" optionId="id" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <CcaAcademicYearInput parentCallback={CcaAcademicYearCallback} />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Existing CCA Schedules</Typography>
            </Grid>

            <Grid item xs={12}>
              <TableContainer component={Paper}>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Term</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Drop-off Type</TableCell>
                      <TableCell>Schedule Detail</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ccaStudentSchedules.length > 0 ? (
                      <>
                        {ccaStudentSchedules.map(
                          (
                            ccaStudentSchedule: ICcaStudentSchedule,
                            key: number,
                          ) => {
                            return (
                              <TableRow key={`cca-schedule-${key}`}>
                                <TableCell>
                                  {(() => {
                                    const term = find(ccaAcademicTerms, [
                                      "id",
                                      ccaStudentSchedule.termId,
                                    ]);

                                    if (term === undefined) {
                                      return;
                                    }

                                    return term.name;
                                  })()}
                                </TableCell>
                                <TableCell>
                                  {`${moment
                                    .utc(ccaStudentSchedule.startDate)
                                    .format(format)} - ${moment
                                    .utc(ccaStudentSchedule.endDate)
                                    .format(format)}`}
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    if (
                                      ccaStudentSchedule.detail === null ||
                                      ccaStudentSchedule.isTaking === false
                                    ) {
                                      return "---";
                                    }

                                    return ccaScheduleDropOffTypeFormatter(
                                      ccaStudentSchedule.dropOffType,
                                      ccaDropOffLocationType,
                                    );
                                  })()}
                                </TableCell>
                                {ccaStudentSchedule.detail === null ||
                                ccaStudentSchedule.isTaking === false ? (
                                  <TableCell>---</TableCell>
                                ) : (
                                  <TableCell>
                                    <CcaScheduleDetail
                                      schedule={ccaStudentSchedule.detail}
                                      dropOffType={
                                        ccaStudentSchedule.dropOffType
                                      }
                                      ccaAreas={ccaAreas}
                                      fixedLocations={fixedLocations}
                                      waves={ccaWaves}
                                    />
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          },
                        )}
                      </>
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4}>
                          {loading ? "Loading..." : "No results found."}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Schedule</Typography>
            </Grid>

            <Grid item xs={4}>
              <StudentScheduleStartDateInput
                source="ccaStartedAt"
                label="Start Date *"
                parentStartDate={moment
                  .utc(ccaAcademicYearResources?.startDate)
                  .format("yyyy-MM-DD")}
                parentEndDate={moment
                  .utc(ccaAcademicYearResources?.endDate)
                  .format("yyyy-MM-DD")}
              />
            </Grid>
            <Grid item xs={4}>
              <CustomEndDateInput
                source="ccaEndedAt"
                label="End Date *"
                parentEndDate={moment
                  .utc(ccaAcademicYearResources?.endDate)
                  .format("yyyy-MM-DD")}
                comparison="ccaStartedAt"
              />
            </Grid>
            <Grid item xs={4}></Grid>

            <Grid item xs={12}>
              <BooleanInput
                source="isNotTaking"
                label="Not Taking"
                defaultValue={false}
                onChange={(e: boolean) => {
                  setIsNotTaking(e);
                  // setIsLoaded(false);
                }}
              />
            </Grid>

            {!isNotTaking && (
              <>
                {school && school.ccaDropOffType === "both" && (
                  <Grid item xs={12}>
                    <RadioButtonGroupInput
                      label="Drop-off Address Type"
                      source="dropOffTypeOption"
                      choices={[
                        {
                          id: "fixed_location",
                          name: "Fixed Location",
                        },
                        {
                          id: "door_to_door",
                          name: "Door-to-door Location",
                        },
                      ]}
                      validate={required()}
                      onChange={(e) => {
                        setCcaDropOffType(e as unknown as string);
                      }}
                    />
                  </Grid>
                )}

                {ccaDropOffType === "fixed_location" && (
                  <>
                    <Grid item xs={12}>
                      <BooleanInput
                        source="isUseOneFixedLocation"
                        label="Use One Fixed Location for All Selected Days"
                        defaultValue={true}
                        onChange={(e: boolean) => {
                          setIsUseOneFixedLocation(e);
                        }}
                      />
                    </Grid>

                    {isUseOneFixedLocation && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="h6">
                            Drop off Location
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <SelectInput
                            source="fixedLocationId"
                            label="Select Fixed Location"
                            choices={fixedLocations.filter(
                              (fl) => fl.status === "active",
                            )}
                            validate={required()}
                          />
                        </Grid>
                        <Grid item xs={8}></Grid>
                      </>
                    )}

                    <Grid item xs={12}>
                      <Typography variant="h6">Days</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <FixedLocationDaysOfWeekInput
                        isUseOneFixedLocation={isUseOneFixedLocation}
                        allowSelectWave={allowSelectWave}
                        schoolId={school.id}
                      />
                    </Grid>
                  </>
                )}

                {ccaDropOffType === "door_to_door" && (
                  <>
                    <DaysOfWeekInput />

                    <Grid item xs={12}>
                      <Typography variant="h6">
                        Pick up & Drop off Address
                      </Typography>
                    </Grid>

                    <Grid item xs={4}>
                      <AddressAutoCompleteInput
                        label="Postal Code *"
                        source="postalCode"
                        referenceSource="ccaLocationData"
                      />
                    </Grid>
                    <Grid item xs={8}></Grid>

                    <Grid item xs={2}>
                      <TextInput label="Unit No." source="unitNumber" />
                    </Grid>
                    <Grid item xs={2}>
                      <TextInput label="Block No." source="blockNumber" />
                    </Grid>
                    <Grid item xs={8}></Grid>

                    <Grid item xs={4}>
                      <TextInput label="Building Name" source="building" />
                    </Grid>
                    <Grid item xs={8}></Grid>

                    <Grid item xs={4}>
                      <TextInput
                        label="Street"
                        source="street"
                        validate={required()}
                      />
                    </Grid>
                    <Grid item xs={8}></Grid>

                    <Grid item xs={4}>
                      <BooleanInput
                        label="Set this address as main address."
                        source="isMain"
                      />
                    </Grid>
                    <Grid item xs={8}></Grid>
                  </>
                )}
              </>
            )}

            {isLoaded && (
              <Grid item xs={12}>
                {resultCalculations.length > 0 ? (
                  <div style={{ marginTop: -20 }}>
                    {resultCalculations.map(
                      (
                        resultCalculation: IResultCcaCalculation,
                        index: number,
                      ) => (
                        <div key={`result-${index}`}>
                          <InitializeCommercialDocuments
                            result={resultCalculation}
                            index={index}
                          />

                          <Grid item xs={12}>
                            <Typography variant="h6">
                              Cost Preview for Rate Chart #
                              {resultCalculation.ccaRateChart.id} -{" "}
                              {resultCalculation.ccaRateChart.name}
                            </Typography>

                            {resultCalculation.transactions.length > 0 ? (
                              <>
                                <TableContainer>
                                  <Table className={classes.table} size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Schedule Date</TableCell>
                                        <TableCell>Amount</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {resultCalculation.transactions.map(
                                        (
                                          item: IResultCalculationItem,
                                          key: number,
                                        ) => {
                                          return (
                                            <TableRow
                                              key={`transaction-${key}`}
                                            >
                                              <TableCell>
                                                {moment
                                                  .utc(item.fromDate)
                                                  .format(format)}{" "}
                                                -{" "}
                                                {moment
                                                  .utc(item.toDate)
                                                  .format(format)}
                                              </TableCell>
                                              <TableCell>
                                                ${" "}
                                                {(item.total / 100).toFixed(2)}
                                              </TableCell>
                                            </TableRow>
                                          );
                                        },
                                      )}

                                      <TableRow>
                                        <TableCell>Total :</TableCell>
                                        <TableCell>
                                          ${" "}
                                          {sumCentsAmountToDolar(
                                            resultCalculation.transactions,
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </TableContainer>

                                <Grid item xs={4}>
                                  <Typography variant="h6" gutterBottom>
                                    Document Note
                                  </Typography>
                                  <CommercialDocumentNoteInput
                                    index={index}
                                    ccaRateChartId={
                                      resultCalculation.ccaRateChart.id
                                    }
                                  />
                                </Grid>
                              </>
                            ) : (
                              <Grid item xs={12}>
                                <Typography variant="h6">
                                  No Change in Cost.
                                </Typography>
                              </Grid>
                            )}
                          </Grid>

                          {Number(
                            sumCentsAmountToDolar(
                              resultCalculation.transactions,
                            ),
                          ) < 0 &&
                            paymentPercentages.length === 1 && (
                              <div style={{ marginTop: 15 }}>
                                <Grid item xs={12}>
                                  <Typography variant="h6" gutterBottom>
                                    Tag Invoice
                                  </Typography>
                                </Grid>

                                <Grid item xs={4}>
                                  <InvoiceInput
                                    label="Select Invoice"
                                    source={`commercialDocumentTags[${index}].invoiceMbcRef`}
                                    ccaRateChartSource={`commercialDocumentTags[${index}].ccaRateChartId`}
                                    ccaRateChartId={
                                      resultCalculation.ccaRateChart.id
                                    }
                                    creditNoteAmount={Math.abs(
                                      Number(
                                        sumCentsAmountToDolar(
                                          resultCalculation.transactions,
                                        ),
                                      ),
                                    )}
                                  />
                                </Grid>
                              </div>
                            )}
                        </div>
                      ),
                    )}
                  </div>
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="h6">No Change in Cost.</Typography>
                  </Grid>
                )}
              </Grid>
            )}

            <Grid item xs={12}>
              <FormDataConsumer>
                {({ formData }) => {
                  formData.dropOffType = ccaDropOffType;
                  formData.allowSelectWave = allowSelectWave;
                  return (
                    <div>
                      <CalculationButton
                        invalid={calculationCostUpdateCcaScheduleValidation(
                          formData,
                        )}
                        setResultCalculations={setResultCalculations}
                        setIsLoaded={setIsLoaded}
                        setIsCalculationButtonClicked={
                          setIsCalculationButtonClicked
                        }
                        formData={formData}
                      />
                    </div>
                  );
                }}
              </FormDataConsumer>
            </Grid>
          </SanitizedGrid>
        </SimpleForm>
      </CreateWrapper>
    </>
  );
};

export default CcaScheduleCreate;
